import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '@/components/PageHeader';
import { getAdminOverview, listWarehouseRecords, type AdminOverviewResponse, type WarehouseRecordItem } from '@/services/adminApi';

type Filters = {
  action: string;
  search: string;
};

const initialFilters: Filters = {
  action: '',
  search: '',
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [items, setItems] = useState<WarehouseRecordItem[]>([]);
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadRecords = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const [recordsResponse, overviewResponse] = await Promise.all([
        listWarehouseRecords({
          limit: 100,
          offset: 0,
          action: filters.action || undefined,
          search: filters.search.trim() || undefined,
        }),
        getAdminOverview(),
      ]);
      setItems(recordsResponse.items);
      setOverview(overviewResponse);
    } catch {
      setErrorMessage('Nie udało się pobrać danych panelu administratora.');
      setItems([]);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRecords();
  }, []);

  const actionLabel = (value: string) => {
    if (value === 'create_varibox') {
      return 'Dodanie Variboxa';
    }
    if (value === 'update_varibox') {
      return 'Edycja Variboxa';
    }
    if (value === 'delete_varibox') {
      return 'Usunięcie Variboxa';
    }
    return value;
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Admin"
        title="Panel kontroli zapisów magazynierów"
        description="Tu masz pełny podgląd kto i kiedy dodał lub edytował wpisy Varibox w magazynie."
        action={<Button variant="contained" onClick={() => void loadRecords()} disabled={loading}>Odśwież</Button>}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            label="Rodzaj operacji"
            value={filters.action}
            onChange={(event) => setFilters((current) => ({ ...current, action: event.target.value }))}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            <MenuItem value="create_varibox">Dodanie Variboxa</MenuItem>
            <MenuItem value="update_varibox">Edycja Variboxa</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Szukaj po osobie, e-mailu lub numerze"
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="outlined" onClick={() => void loadRecords()} disabled={loading}>Zastosuj filtry</Button>
      </Stack>

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Alert severity="info" action={<Button color="inherit" onClick={() => navigate('/locations')}>Przejdź do Lokalizacji</Button>}>
        Dodawanie podmagazynów i węzłów klientów zostało przeniesione do widoku Lokalizacje z mapą.
      </Alert>

      <Card>
        <CardContent>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="h6">Gdzie trzymane sa dane</Typography>
            <Typography variant="body2" color="text.secondary">
              Silnik: {overview?.storage.engine ?? '-'} | Host: {overview?.storage.host ?? '-'}
              {overview?.storage.port ? `:${overview.storage.port}` : ''} | Baza: {overview?.storage.database ?? '-'}
            </Typography>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Uzytkownicy</Typography>
                  <Typography variant="h5">{overview?.counts.users ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Lokalizacje</Typography>
                  <Typography variant="h5">{overview?.counts.locations ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Variboxy</Typography>
                  <Typography variant="h5">{overview?.counts.variboxes ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Logi operacji</Typography>
                  <Typography variant="h5">{overview?.counts.warehouse_records ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="h6">Zapisy operacji</Typography>
            <Typography variant="body2" color="text.secondary">Łącznie wpisów: {items.length}</Typography>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Czas</TableCell>
                <TableCell>Magazynier</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Rola</TableCell>
                <TableCell>Operacja</TableCell>
                <TableCell>Obiekt</TableCell>
                <TableCell>Szczegóły</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
                  <TableCell>{item.actor_name}</TableCell>
                  <TableCell>{item.actor_email}</TableCell>
                  <TableCell>{item.actor_role}</TableCell>
                  <TableCell>{actionLabel(item.action)}</TableCell>
                  <TableCell>{item.target_label ?? '-'}</TableCell>
                  <TableCell>{item.details_json ?? '-'}</TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>Brak zapisów do wyświetlenia.</TableCell>
                </TableRow>
              ) : null}
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>Ładowanie zapisów...</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Stack>
  );
}